import { useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import VideoCard from '@/components/base/VideoCard';
import { categories, categorySlugMap } from '@/mocks/categories';
import { videos } from '@/mocks/videos';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const categoryName = slug ? categorySlugMap[slug] || '分类' : '分类';
  const categoryVideos = videos.filter((v) => v.categorySlug === slug);
  const categoryInfo = categories.find((c) => c.slug === slug);

  const formatViews = (views: number): string => {
    if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
    return views.toString();
  };

  const categorySchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: `${categoryName} - 蜜桃tv`,
      description: `蜜桃tv ${categoryName} 频道提供海量精选${categoryName}内容，高清流畅播放，随时随地畅享优质${categoryName}资源。立即访问蜜桃tv ${categoryName}频道。`,
      url: `https://videomio.github.io/category/${slug}`,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: categoryVideos.slice(0, 10).map((v, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'VideoObject',
          name: v.title,
          description: v.summary,
          thumbnailUrl: v.thumbnail,
          url: `https://videomio.github.io/article/${v.slug}`,
        },
      })),
    },
  ];

  return (
    <>
      <SEOHead
        title={`${categoryName} - 蜜桃tv | 海量精选${categoryName}高清在线观看`}
        description={`蜜桃tv ${categoryName} 频道提供海量精选${categoryName}内容，涵盖热门${categoryName}、最新${categoryName}、高分${categoryName}推荐，高清流畅播放，随时随地畅享优质${categoryName}资源。立即访问蜜桃tv ${categoryName}频道，发现更多精彩${categoryName}内容。`}
        keywords={`蜜桃tv,蜜桃tv ${categoryName},蜜桃tv ${categoryName}在线观看,${categoryName},蜜桃tv高清,蜜桃tv分类`}
        canonicalUrl={`https://videomio.github.io/category/${slug}`}
        schemaData={categorySchema}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <ScrollToTop />

        <main className="pt-16">
          {/* Breadcrumb */}
          <div className="max-w-[1280px] mx-auto pt-4">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '分类浏览', href: '/category/dianshi' },
                { label: categoryName },
              ]}
            />
          </div>

          {/* Category Header */}
          <section className="w-full px-4 md:px-6 py-8">
            <div className="max-w-[1280px] mx-auto">
              <div className="flex items-center gap-4 mb-4">
                <span className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center">
                  <i className={`${categoryInfo?.icon || 'ri-folders-line'} text-2xl text-primary-600`}></i>
                </span>
                <div>
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                    {categoryName}
                  </h1>
                  <p className="text-sm text-foreground-600 mt-1">
                    {categoryInfo?.description || `海量精选${categoryName}内容`} · 共{' '}
                    {categoryVideos.length} 部精选内容
                  </p>
                </div>
              </div>

              {/* Sub category tabs */}
              <div className="flex items-center gap-2 flex-wrap mt-6">
                <div className="inline-flex items-center bg-background-100 rounded-full p-1 border border-background-200/70">
                  {[
                    { key: 'all', label: '全部' },
                    { key: 'hot', label: '最热' },
                    { key: 'new', label: '最新' },
                    { key: 'top', label: '高分' },
                  ].map((tab) => (
                    <button
                      key={tab.key}
                      className="px-5 py-2 rounded-full text-sm font-medium text-foreground-600 hover:text-foreground-950 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Category Quick Nav - other categories */}
          <section className="w-full px-4 md:px-6 pb-4">
            <div className="max-w-[1280px] mx-auto">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-foreground-500 font-medium">全部分类：</span>
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => {
                      window.REACT_APP_NAVIGATE(`/category/${cat.slug}`);
                    }}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      cat.slug === slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    <span className="flex items-center gap-1">
                      <span className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className={`${cat.icon} text-xs`}></i>
                      </span>
                      {cat.name}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* Video Grid */}
          <section className="w-full px-4 md:px-6 py-6">
            <div className="max-w-[1280px] mx-auto">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {categoryVideos.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
              {categoryVideos.length === 0 && (
                <div className="text-center py-16">
                  <span className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                  </span>
                  <p className="text-foreground-500">暂无该分类的内容，敬请期待</p>
                </div>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}